import moment from 'moment'
import { defaultTraceParameters } from '@/components/helpers/plotly_style'

const DEFAULT_LIGHT_GREY = '#b0b0b0'
const DEFAULT_MAX_BLUE = '#1f77b4'

export default class ReportDataHelper {

  static _aggregateValues(traces, method, plotType) {
    if (!method || traces.length === 0) return traces

    const aggregatedX = traces[0].x
    const aggregatedY = new Array(aggregatedX.length).fill(0)

    traces.forEach(trace => {
      trace.y.forEach((val, index) => {
        if (method === 'sum') {
          aggregatedY[index] += val
        } else if (method === 'min') {
          if (index in aggregatedY) {
            aggregatedY[index] = Math.min(aggregatedY[index], val)
          } else {
            aggregatedY[index] = val
          }
        } else if (method === 'max') {
          if (index in aggregatedY) {
            aggregatedY[index] = Math.max(aggregatedY[index], val)
          } else {
            aggregatedY[index] = val
          }
        } else if (method === 'count') {
          aggregatedY[index] += 1
        }
      })
    })

    return [{
      x: aggregatedX,
      y: aggregatedY,
      type: plotType,
      name: 'Overall',
      ...defaultTraceParameters
    }]
  }

  /**
   * A private helper function that handles both "live data" (multi-stat traces)
   * and "stats data" (also multi-stat, but one trace per device).
   *
   * @param {any} rawData
   * @param {string} labelField
   * @param {string|Array} valueField - A single string or array of strings
   * @param {string} orderField
   * @param {any} nullValue
   * @param {boolean} isStatsFormat
   * @param {boolean} isDate
   * @param {string} plotType
   * @param {boolean} hideZeroValues
   * @param {number} valueMultiple
   * @param {string} aggregationMethod
   * @param {boolean} highlightMax
   */
  static _generateCategoricalPlotData(
    rawData,
    labelField,
    valueField,
    orderField,
    nullValue,
    isStatsFormat = false,
    isDate = false,
    plotType = 'bar',
    hideZeroValues = false,
    valueMultiple = 1,
    aggregationMethod = null,
    highlightMax = false
  ) {
    if (!rawData) {
      return [{ x: [], y: [], type: 'bar' }]
    }

    // Normalize valueField into an array
    const allFields = Array.isArray(valueField) ? valueField : [valueField]

    // ============ CASE 1: LIVE DATA ============
    if (!isStatsFormat) {
      // rawData is array of objects. We'll produce one trace per statistic in allFields.

      // First, build an array of device labels from `labelField`,
      // with the assumption that all stats share the same device set.
      // We'll also handle ordering by the first stat.

      // 1) Gather the row data for the first statistic to handle sorting
      let baseValues = []
      let labels = []
      let orderValues = []
      let orderDirection = 1

      if (orderField) {
        if (orderField.startsWith('-')) {
          orderDirection = -1
          orderField = orderField.slice(1)
        } else if (orderField.startsWith('+')) {
          orderField = orderField.slice(1)
        }
      }

      rawData.forEach(row => {
        const lbl = ReportDataHelper.getNestedField(row, labelField, nullValue)
        const firstStatVal = ReportDataHelper.getNestedField(row, allFields[0], nullValue)
        labels.push(lbl)
        baseValues.push(firstStatVal)

        if (orderField) {
          const orderVal = ReportDataHelper.getNestedField(row, orderField, nullValue)
          orderValues.push(orderVal)
        }
      })

      // 2) If there's an order field, generate an index-based sort
      let sortedIndices = [...Array(labels.length).keys()] // 0..n
      if (orderField && orderValues.length > 0) {
        sortedIndices.sort((a, b) => {
          return orderDirection * (orderValues[a] - orderValues[b])
        })
      }

      // 3) For each statistic in allFields, produce a trace with X=labels, Y=that stat
      let allTraces = []

      allFields.forEach((statName, statIndex) => {
        // gather Y values from rawData
        let valuesForThisStat = rawData.map(row => {
          let val = ReportDataHelper.getNestedField(row, statName, nullValue)
          return val * valueMultiple
        })

        // apply the same sorting
        let sortedLabels = sortedIndices.map(i => labels[i])
        let sortedValues = sortedIndices.map(i => valuesForThisStat[i])

        // Possibly filter out the entire trace if all zero
        if (hideZeroValues && sortedValues.every(v => Math.abs(v) < 1e-9)) {
          return
        }

        let traceName = statName
        // If you want a more user-friendly label, you could do a lookup here
        allTraces.push({
          x: sortedLabels,
          y: sortedValues,
          type: plotType,
          name: allFields.length > 1 ? statName : undefined, // Only show trace name if multiple fields
          ...defaultTraceParameters
        })
      })

      // 4) If aggregator is requested, combine into single trace
      let finalTraces = aggregationMethod
        ? ReportDataHelper._aggregateValues(allTraces, aggregationMethod, plotType)
        : allTraces

      // 5) If highlightMax, find the max in each trace and color it
      if (highlightMax) {
        finalTraces.forEach(tr => {
          let maxIndex = 0
          let maxVal = Number.NEGATIVE_INFINITY
          tr.y.forEach((v, i) => {
            if (v > maxVal) {
              maxVal = v
              maxIndex = i
            }
          })
          let defaultColor = DEFAULT_LIGHT_GREY
          let maxColor = DEFAULT_MAX_BLUE
          tr.marker = {
            color: tr.y.map((_, i) => (i === maxIndex ? maxColor : defaultColor))
          }
        })
      }

      return finalTraces
    }

    // ============ CASE 2: STATS DATA ============
    // rawData has { labels: [...], stats: {...} }. We'll produce multiple sets of traces if multiple stats.
    const { labels, stats } = rawData
    if (!labels || !stats) {
      return [{ x: [], y: [], type: 'bar' }]
    }

    // 1) Gather device lists for each statistic. We'll produce (deviceCount) traces per statistic,
    // or aggregator if that’s set.
    let allTraces = []

    // We'll produce device-based traces for each statistic
    allFields.forEach((statName, sIndex) => {
      let deviceData = stats[statName]
      if (!deviceData) return

      // For each device, produce x=labels, y=values
      for (const deviceId of Object.keys(deviceData)) {
        const xVals = []
        const yVals = []
        labels.forEach(dateLabel => {
          // parse date if needed
          const xVal = isDate
            ? moment(dateLabel, 'YYYY/MM/DD').format('YYYY-MM-DD')
            : dateLabel
          xVals.push(xVal)

          // numeric fallback
          const val = deviceData[deviceId][dateLabel] ?? nullValue
          yVals.push(val * valueMultiple)
        })

        // Possibly skip the trace if all zero
        if (hideZeroValues && yVals.every(v => Math.abs(v) < 1e-9)) {
          continue
        }

        // If multiple stats, label them "<Device> - <Stat>"
        let traceName
        if (allFields.length > 1) {
          traceName = `${deviceId} - ${statName}`
        } else {
          traceName = deviceId
        }

        allTraces.push({
          x: xVals,
          y: yVals,
          type: plotType,
          name: traceName,
          ...defaultTraceParameters
        })
      }
    })

    // 2) If aggregator is provided, combine everything
    let finalTraces = aggregationMethod
      ? ReportDataHelper._aggregateValues(allTraces, aggregationMethod, plotType)
      : allTraces

    // 3) If highlightMax, highlight the max point in each trace
    if (highlightMax) {
      finalTraces.forEach(trace => {
        let maxIndex = 0
        let maxValue = Number.NEGATIVE_INFINITY
        trace.y.forEach((v, i) => {
          if (v > maxValue) {
            maxValue = v
            maxIndex = i
          }
        })
        let defaultColor = DEFAULT_LIGHT_GREY
        let maxColor = DEFAULT_MAX_BLUE
        trace.marker = {
          color: trace.y.map((_, i) => (i === maxIndex ? maxColor : defaultColor))
        }
      })
    }

    return finalTraces
  }

  /**
   * For live data: supports multiple stats, returning multiple traces if `valueField` is an array.
   */
  static generateCategoricalPlotLiveData(
    rawData,
    labelField,
    valueField,      // can be string or array
    orderField,
    nullValue,
    plotType = 'bar',
    hideZeroValues = false,
    valueMultiple = 1,
    aggregationMethod = null,
    highlightMax = false
  ) {
    return ReportDataHelper._generateCategoricalPlotData(
      rawData,
      labelField,
      valueField,
      orderField,
      nullValue,
      false,   // isStatsFormat = false
      false,   // isDate = false
      plotType,
      hideZeroValues,
      valueMultiple,
      aggregationMethod,
      highlightMax
    )
  }

  /**
   * For stats data: supports multiple stats, returning (deviceCount * statCount) traces,
   * or a single aggregated trace if aggregator is used.
   */
  static generateCategoricalPlotStatsData(
    rawData,
    valueField,      // can be string or array
    nullValue,
    isDate = false,
    plotType = 'bar',
    hideZeroValues = false,
    valueMultiple = 1,
    aggregration_mode = null,
    highlightMax = false
  ) {
    return ReportDataHelper._generateCategoricalPlotData(
      rawData,
      null,       // not needed
      valueField,
      null,       // no ordering
      nullValue,
      true,       // isStatsFormat = true
      isDate,
      plotType,
      hideZeroValues,
      valueMultiple,
      aggregration_mode,
      highlightMax
    )
  }

  /**
   * Helper to retrieve a nested property from an object using dot-notation.
   */
  static getNestedField(obj, fieldPath, defaultValue) {
    if (!obj || typeof fieldPath !== 'string') {
      return defaultValue
    }
    const parts = fieldPath.split('.')
    let current = obj
    for (const p of parts) {
      if (current[p] == null) {
        return defaultValue
      }
      current = current[p]
    }
    return current
  }

  // ... histogram methods unchanged ...
}
